/**
 * Timeline Reducer
 *
 * Manages state transitions for zoom/pan operations.
 */

import type { TimelineContextState, TimelineAction } from './types'
import { DEFAULT_ZOOM_CONSTRAINTS, DEFAULT_TRANSFORM } from './types'

/**
 * Clamp a value between min and max
 */
function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

/**
 * Constrain transform to valid bounds
 * Only constrains zoom (k), allows infinite panning (x)
 */
function constrainTransform(state: TimelineContextState, transform: typeof DEFAULT_TRANSFORM): typeof DEFAULT_TRANSFORM {
  const { constraints } = state
  const k = clamp(transform.k, constraints.minScale, constraints.maxScale)
  return { x: transform.x, y: 0, k }
}

/**
 * Timeline reducer function
 */
export function timelineReducer(
  state: TimelineContextState,
  action: TimelineAction
): TimelineContextState {
  switch (action.type) {
    case 'SET_TRANSFORM': {
      const transform = constrainTransform(state, action.payload)
      return { ...state, transform }
    }

    case 'ZOOM_IN': {
      const newK = state.transform.k * state.constraints.zoomStep
      const transform = constrainTransform(state, { ...state.transform, k: newK })
      return { ...state, transform }
    }

    case 'ZOOM_OUT': {
      const newK = state.transform.k / state.constraints.zoomStep
      const transform = constrainTransform(state, { ...state.transform, k: newK })
      return { ...state, transform }
    }

    case 'RESET': {
      return { ...state, transform: DEFAULT_TRANSFORM }
    }

    case 'PAN': {
      const newX = state.transform.x + action.payload
      const transform = constrainTransform(state, { ...state.transform, x: newX })
      return { ...state, transform }
    }

    case 'SET_DOMAIN': {
      return { ...state, domain: action.payload }
    }

    case 'SET_CONTAINER_WIDTH': {
      return { ...state, containerWidth: action.payload }
    }

    case 'SET_ANIMATING': {
      return { ...state, isAnimating: action.payload }
    }

    case 'SET_CONSTRAINTS': {
      const constraints = { ...state.constraints, ...action.payload }
      // Re-constrain transform with new constraints
      const transform = constrainTransform({ ...state, constraints }, state.transform)
      return { ...state, constraints, transform }
    }

    default:
      return state
  }
}

/**
 * Create initial state for the timeline context
 */
export function createInitialState(
  containerWidth: number,
  constraints?: Partial<typeof DEFAULT_ZOOM_CONSTRAINTS>
): TimelineContextState {
  return {
    transform: DEFAULT_TRANSFORM,
    domain: {
      minDate: Date.now() - 30 * 24 * 60 * 60 * 1000, // 30 days ago
      maxDate: Date.now() + 30 * 24 * 60 * 60 * 1000, // 30 days from now
      span: 60 * 24 * 60 * 60 * 1000, // 60 days
      padding: 7 * 24 * 60 * 60 * 1000, // 7 days
    },
    constraints: { ...DEFAULT_ZOOM_CONSTRAINTS, ...constraints },
    containerWidth,
    isAnimating: false,
  }
}
